import { useState, useEffect, useCallback, useMemo, useRef } from "react";

/**
 * useInfiniteTable - Custom hook providing seamless continuous scrolling
 * and arrow-key navigation across large table datasets without pagination walls.
 *
 * @param {Array} items        - Full filtered dataset array
 * @param {number} initialCount - Initial rows to render (default: 100)
 * @param {number} step         - Rows to append when scrolling or arrow-navigating near bottom (default: 100)
 */
export function useInfiniteTable({
  items = [],
  initialCount = 100,
  step = 100,
}) {
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const tableContainerRef = useRef(null);

  // Reset visibleCount & selection when items change (search query, filter, etc.)
  useEffect(() => {
    setVisibleCount(initialCount);
    setSelectedIndex((prev) => (prev !== null && prev < items.length ? prev : null));
  }, [items, initialCount]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => {
      if (prev >= items.length) return prev;
      return Math.min(items.length, prev + step);
    });
  }, [items.length, step]);

  const loadAll = useCallback(() => {
    setVisibleCount(items.length);
  }, [items.length]);

  const displayedRows = useMemo(
    () => items.slice(0, visibleCount),
    [items, visibleCount]
  );

  // Continuous infinite scroll on container scroll
  const handleScroll = useCallback(
    (e) => {
      const target = e.currentTarget;
      if (!target) return;
      const { scrollTop, clientHeight, scrollHeight } = target;
      if (scrollHeight - (scrollTop + clientHeight) < 250) {
        loadMore();
      }
    },
    [loadMore]
  );

  // Keyboard navigation: ArrowUp, ArrowDown, PageUp, PageDown, Home, End
  const handleKeyDown = useCallback(
    (e) => {
      if (!items.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev === null ? 0 : Math.min(items.length - 1, prev + 1);
          if (next >= visibleCount - 15) {
            loadMore();
          }
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev === null ? 0 : Math.max(0, prev - 1)));
      } else if (e.key === "PageDown") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev === null ? 20 : Math.min(items.length - 1, prev + 20);
          if (next >= visibleCount - 15) {
            loadMore();
          }
          return next;
        });
      } else if (e.key === "PageUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev === null ? 0 : Math.max(0, prev - 20)));
      } else if (e.key === "Home") {
        e.preventDefault();
        setSelectedIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        loadAll();
        setSelectedIndex(items.length - 1);
      }
    },
    [items.length, visibleCount, loadMore, loadAll]
  );

  // Auto-scroll selected row into view when navigating via arrow keys
  useEffect(() => {
    if (selectedIndex === null || !tableContainerRef.current) return;
    const container = tableContainerRef.current;
    const selectedElem = container.querySelector(`[data-row-index="${selectedIndex}"]`);
    if (selectedElem) {
      selectedElem.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [selectedIndex]);

  return {
    displayedRows,
    visibleCount,
    totalCount: items.length,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleKeyDown,
    loadMore,
    loadAll,
    hasMore: visibleCount < items.length,
  };
}

export default useInfiniteTable;
